import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { useClasses } from "@api/class.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { formatIsoDateForDisplay } from "@helpers/date";
import {
    SCHEDULE_APPLICATIONS_EMPTY_VALUE,
    ScheduleApplicationRow,
    hasScheduleApplicationClasses,
} from "../scheduleApplicationRowHelpers";
import { useScheduleApplicationsTableMeta } from "../scheduleApplicationsTableMeta";

export const ScheduleApplicationDateCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const rowData = row.original;
        return (
            <ViewTableCell>
                {rowData.date
                    ? formatIsoDateForDisplay(rowData.date)
                    : SCHEDULE_APPLICATIONS_EMPTY_VALUE}
            </ViewTableCell>
        );
    },
);
ScheduleApplicationDateCell.displayName = "ScheduleApplicationDateCell";

export const ScheduleApplicationEntityCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ScheduleApplicationRow, number>) => {
        const meta = useScheduleApplicationsTableMeta(table);
        const rowData = row.original;
        const entity = meta.entitiesById?.get(rowData.entityId);
        if (!entity) {
            return (
                <ViewTableCell>
                    {SCHEDULE_APPLICATIONS_EMPTY_VALUE}
                </ViewTableCell>
            );
        }
        return (
            <ViewTableCell>
                <EntityAvatar size="xs" entity={entity} />
            </ViewTableCell>
        );
    },
);
ScheduleApplicationEntityCell.displayName = "ScheduleApplicationEntityCell";

export const ScheduleApplicationCounterpartyCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const rowData = row.original;
        if (!hasScheduleApplicationClasses(rowData)) {
            return null;
        }
        return (
            <ViewTableCell>
                {rowData.counterpartyName ?? SCHEDULE_APPLICATIONS_EMPTY_VALUE}
            </ViewTableCell>
        );
    },
);
ScheduleApplicationCounterpartyCell.displayName =
    "ScheduleApplicationCounterpartyCell";

/**
 * A schedule template carries no class of its own: the classes shown here are
 * the ones coded on the application's primary source, which the recognition
 * rows inherit.
 */
export const ScheduleApplicationClassCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const { classesMap } = useClasses();
        const rowData = row.original;
        const classIds = hasScheduleApplicationClasses(rowData)
            ? rowData.classIds
            : [];
        const classes = classIds.flatMap((classId) => {
            const classItem = classesMap.get(classId);
            return classItem ? [classItem] : [];
        });
        if (classes.length === 0) {
            return (
                <ViewTableCell>
                    {SCHEDULE_APPLICATIONS_EMPTY_VALUE}
                </ViewTableCell>
            );
        }
        return (
            <ViewTableCell>
                <ClassAvatarGroup classes={classes} max={3} />
            </ViewTableCell>
        );
    },
);
ScheduleApplicationClassCell.displayName = "ScheduleApplicationClassCell";

export const ScheduleApplicationGroupCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const rowData = row.original;
        if (rowData.rowType !== "application") {
            return null;
        }
        return <ViewTableCell>{rowData.group}</ViewTableCell>;
    },
);
ScheduleApplicationGroupCell.displayName = "ScheduleApplicationGroupCell";

export const ScheduleApplicationTemplateCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const rowData = row.original;
        if (rowData.rowType !== "application") {
            return null;
        }
        return <ViewTableCell>{rowData.templateName}</ViewTableCell>;
    },
);
ScheduleApplicationTemplateCell.displayName = "ScheduleApplicationTemplateCell";

export const ScheduleApplicationSplitCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const rowData = row.original;
        return (
            <ViewTableCell>
                {rowData.splitLabel ?? SCHEDULE_APPLICATIONS_EMPTY_VALUE}
            </ViewTableCell>
        );
    },
);
ScheduleApplicationSplitCell.displayName = "ScheduleApplicationSplitCell";
